/**
 * API 客户端
 */
import type {
  LoginRequest,
  LoginResponse,
  User,
  HistoryResponse,
  UploadResponse,
  UploadsListResponse,
  KeysListResponse,
  AddKeyRequest,
  AddKeyResponse,
  StatsResponse,
  ChangePasswordRequest,
  Message,
  SkillsListResponse,
  SkillOperationResponse,
} from './types.js';

export type { Message };

/**
 * API 客户端类
 */
export class APIClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * 设置认证 Token
   */
  setToken(token: string): void {
    this.token = token;
  }

  /**
   * 清除认证 Token
   */
  clearToken(): void {
    this.token = null;
  }

  /**
   * 获取认证头
   */
  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    return headers;
  }

  /**
   * 发送请求
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = {
      ...this.getAuthHeaders(),
      ...options.headers,
    };

    const config: RequestInit = {
      ...options,
      headers,
    };

    const response = await fetch(url, config);

    // 先检查响应状态，如果是错误状态则尝试解析错误信息
    if (!response.ok) {
      // 尝试解析 JSON 错误信息
      let errorMessage = `请求失败: ${response.status}`;
      try {
        const errorData = await response.json() as { error?: string };
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch {
        // JSON 解析失败，使用默认错误消息
      }
      throw new APIError(errorMessage);
    }

    // 成功响应，解析 JSON
    const data = (await response.json()) as T;
    return data;
  }

  /**
   * GET 请求
   */
  private async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  /**
   * POST 请求
   */
  private async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * PUT 请求
   */
  private async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * DELETE 请求
   */
  private async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * 上传文件
   */
  private async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {};

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(data.error || `上传失败: ${response.status}`);
    }

    return data as T;
  }

  // ========== 认证 API ==========

  /**
   * 登录
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    return this.post<LoginResponse>('/api/auth/login', credentials);
  }

  /**
   * 获取当前用户信息
   */
  async getMe(): Promise<User> {
    return this.get<User>('/api/auth/me');
  }

  /**
   * 登出
   */
  async logout(): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }>('/api/auth/logout');
  }

  /**
   * 修改密码
   */
  async changePassword(data: ChangePasswordRequest): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }>('/api/auth/change-password', data);
  }

  // ========== 消息 API ==========

  /**
   * 获取消息历史
   */
  async getHistory(limit = 100): Promise<HistoryResponse> {
    return this.get<HistoryResponse>(`/api/history?limit=${limit}`);
  }

  /**
   * 获取更多历史消息
   */
  async getMoreHistory(before: string, limit = 50): Promise<HistoryResponse> {
    return this.get<HistoryResponse>(
      `/api/history?before=${before}&limit=${limit}`,
    );
  }

  // ========== 文件上传 API ==========

  /**
   * 上传文件
   */
  async uploadFile(file: File): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.upload<UploadResponse>('/api/upload', formData);
  }

  /**
   * 获取上传文件列表
   */
  async getUploads(): Promise<UploadsListResponse> {
    return this.get<UploadsListResponse>('/api/uploads');
  }

  // ========== API Key 管理 API ==========

  /**
   * 获取所有 API Keys
   */
  async getKeys(): Promise<KeysListResponse> {
    return this.get<KeysListResponse>('/api/keys');
  }

  /**
   * 添加 API Key
   */
  async addKey(keyData: AddKeyRequest): Promise<AddKeyResponse> {
    return this.post<AddKeyResponse>('/api/keys', keyData);
  }

  /**
   * 删除 API Key
   */
  async deleteKey(keyId: string): Promise<{ success: boolean }> {
    return this.delete<{ success: boolean }>(`/api/keys/${keyId}`);
  }

  /**
   * 切换 API Key 状态
   */
  async toggleKey(keyId: string, enabled: boolean): Promise<{ success: boolean }> {
    return this.put<{ success: boolean }>(`/api/keys/${keyId}/toggle`, { enabled });
  }

  /**
   * 重置 API Key 错误计数
   */
  async resetKey(keyId: string): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }>(`/api/keys/${keyId}/reset`);
  }

  /**
   * 更新 API Key
   */
  async updateKey(
    keyId: string,
    updates: { apiKey?: string; baseUrl?: string; model?: string; priority?: number },
  ): Promise<{ success: boolean }> {
    return this.put<{ success: boolean }>(`/api/keys/${keyId}`, updates);
  }

  /**
   * 选择 API Key（手动模式）
   */
  async selectKey(keyId: string): Promise<{ success: boolean; keyId: string }> {
    return this.post<{ success: boolean; keyId: string }>(`/api/keys/${keyId}/select`);
  }

  /**
   * 选择 API Key（强制模式，用于容器忙碌时强制切换）
   */
  async selectKeyForce(keyId: string): Promise<{ success: boolean; keyId: string }> {
    return this.post<{ success: boolean; keyId: string }>(`/api/keys/${keyId}/select`, { force: true });
  }

  /**
   * 清除 API Key 选择（切换回自动模式）
   */
  async clearSelection(): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }>('/api/keys/clear-selection');
  }

  /**
   * 设置 API Key 每日限额
   */
  async setDailyLimit(keyId: string, limit: number): Promise<{ success: boolean; dailyLimit: number }> {
    return this.put<{ success: boolean; dailyLimit: number }>(`/api/keys/${keyId}/daily-limit`, { limit });
  }

  /**
   * 重置 API Key 每日使用量
   */
  async resetUsage(keyId: string): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }>(`/api/keys/${keyId}/reset-usage`);
  }

  // ========== 统计 API ==========

  /**
   * 获取统计信息
   */
  async getStats(): Promise<StatsResponse> {
    return this.get<StatsResponse>('/api/stats');
  }

  /**
   * 获取所有 Agent 状态
   */
  async getAgentStatus(): Promise<{
    agents: Array<{
      groupJid: string;
      groupFolder: string | null;
      status: 'active' | 'idle' | 'error' | 'inactive';
      health: 'healthy' | 'unhealthy' | 'unknown';
      containerName: string | null;
      isTaskContainer: boolean;
      runningTaskId: string | null;
      pendingMessages: number;
      pendingTasks: number;
      currentApiKeyId?: string;
      containers: Array<{
        id: string;
        status: 'ready' | 'running' | 'idle' | 'error';
        keyId?: string;
      }>;
    }>;
    summary: {
      totalAgents: number;
      activeAgents: number;
      idleAgents: number;
      inactiveAgents: number;
      unhealthyAgents: number;
    };
  }> {
    return this.get('/api/agents/status');
  }

  // ========== Grob API 配置 ==========

  /**
   * 获取 Grob 状态
   */
  async getGrobStatus(): Promise<{
    running: boolean;
    port: number;
    providersCount: number;
    modelsCount: number;
    defaultModel: string;
    spend?: number;
  }> {
    return this.get('/api/grob/status');
  }

  /**
   * 获取 Grob 配置
   */
  async getGrobConfig(): Promise<{
    providers: Array<{
      name: string;
      provider_type: string;
      base_url: string;
      api_key: string;
      models: string[];
    }>;
    models: Array<{
      name: string;
      mappings: Array<{
        provider: string;
        actual_model: string;
        priority: number;
      }>;
    }>;
  }> {
    return this.get('/api/grob/config');
  }

  /**
   * 添加 Grob Provider
   */
  async addGrobProvider(data: {
    name: string;
    provider_type: string;
    base_url: string;
    api_key: string;
    model: string;
    priority: number;
  }): Promise<{ success: boolean }> {
    return this.post('/api/grob/providers', data);
  }

  /**
   * 删除 Grob Provider
   */
  async deleteGrobProvider(name: string): Promise<{ success: boolean }> {
    return this.delete(`/api/grob/providers/${encodeURIComponent(name)}`);
  }

  /**
   * 更新 Grob Provider
   */
  async updateGrobProvider(name: string, data: {
    base_url?: string;
    api_key?: string;
    model?: string;
    priority?: number;
  }): Promise<{ success: boolean }> {
    return this.put(`/api/grob/providers/${encodeURIComponent(name)}`, data);
  }

  /**
   * 重启 Grob 服务
   */
  async restartGrob(): Promise<{ success: boolean }> {
    return this.post('/api/grob/restart', {});
  }

  // ========== Skills 管理 API ==========

  /**
   * 获取所有 Skills
   */
  async getSkills(): Promise<SkillsListResponse> {
    return this.get<SkillsListResponse>('/api/skills');
  }

  /**
   * 安装 Skill
   */
  async installSkill(skillId: string): Promise<SkillOperationResponse> {
    return this.post<SkillOperationResponse>(`/api/skills/${encodeURIComponent(skillId)}/install`);
  }

  /**
   * 卸载 Skill
   */
  async uninstallSkill(skillId: string): Promise<SkillOperationResponse> {
    return this.post<SkillOperationResponse>(`/api/skills/${encodeURIComponent(skillId)}/uninstall`);
  }

  /**
   * 重启服务
   */
  async restartService(): Promise<{ success: boolean }> {
    return this.post('/api/skills/restart');
  }
}

/**
 * API 错误类
 */
export class APIError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'APIError';
  }
}

/**
 * 创建单例实例
 */
let apiClient: APIClient | null = null;

export function createAPIClient(baseUrl: string): APIClient {
  if (!apiClient) {
    apiClient = new APIClient(baseUrl);
  }
  return apiClient;
}

export function getAPIClient(): APIClient | null {
  return apiClient;
}
